import { DEFAULT_CURRENCY, DEFAULT_QUANTITY, DEFAULT_SEGMENT, normalizeCommitmentType } from '../../core/normalizers';
import { GTMItemServiceType, GTMItemType, GTMOrderType } from '../../types';
import type { TrackingCommerce, TrackingItem } from '../../types';
import type { OTTPlanEventData, OTTPlanSource, TVOrderingEventData } from './types';

const PAYMENT_METHOD_WITH_PLAN = 'With Plan';

const getNumber = (value?: string | number) => {
    if (typeof value === 'number') {
        return value;
    }

    return value ? parseFloat(value) : 0;
};

const roundToTwo = (value: number) => Math.round(value * 100) / 100;
const getDiscount = (originalPrice: number, price: number) =>
    originalPrice ? roundToTwo(Math.max(originalPrice - price, 0)) : 0;

const mapOTTPlanToTrackingItem = (plan: OTTPlanSource, index: number): TrackingItem => {
    const originalPrice = getNumber(plan.pricing?.old);
    const regularPrice = getNumber(plan.pricing?.regular);
    const eventualPrice = getNumber(plan.progressivePricing?.eventualPrice?.price);
    const price = regularPrice || eventualPrice || 0;

    return {
        type: GTMItemType.Product,
        id: plan.id,
        name: plan.name || '',
        brand: '',
        variant: '',
        category: 'Services',
        listId: '',
        listName: 'OTT',
        index,
        originalPrice: originalPrice || price || 0,
        price: price || 0,
        discount: getDiscount(originalPrice, price),
        quantity: DEFAULT_QUANTITY,
        commitmentType: normalizeCommitmentType(plan.planOptionName),
        serviceType: GTMItemServiceType.NewSale,
    };
};

export const mapOTTPlansToTrackingCommerce = (data: OTTPlanEventData): TrackingCommerce => ({
    orderType: GTMOrderType.Services,
    segment: DEFAULT_SEGMENT,
    currency: DEFAULT_CURRENCY,
    value: 0,
    itemListName: 'OTT',
    paymentMethod: PAYMENT_METHOD_WITH_PLAN,
    transactionId: '',
    items: data.plans.map(mapOTTPlanToTrackingItem),
});

const TV_ORDERING_LIST_NAME = {
    [GTMOrderType.Equipment]: 'Priedėlis',
    [GTMOrderType.Services]: 'OTT',
} as const;

const TV_ORDERING_ITEM_CATEGORY = {
    [GTMOrderType.Equipment]: 'Equipment',
    [GTMOrderType.Services]: 'Services',
} as const;

const TV_ORDERING_FIXED_ITEM_NAME = {
    [GTMOrderType.Equipment]: 'Priedėlis',
    [GTMOrderType.Services]: '',
} as const;

const getTVOrderingOrderType = (orderType?: string) =>
    orderType === GTMOrderType.Equipment ? GTMOrderType.Equipment : GTMOrderType.Services;

const mapTVOrderingToTrackingItem = (data: TVOrderingEventData): TrackingItem => {
    const orderType = getTVOrderingOrderType(data.orderType);
    const fixedItemName = TV_ORDERING_FIXED_ITEM_NAME[orderType];
    const originalPrice = data.originalPrice || 0;
    const price = data.price || 0;

    return {
        type: GTMItemType.Product,
        id: fixedItemName || data.planName?.toLowerCase() || '',
        name: fixedItemName || data.itemName || data.planName || '',
        brand: '',
        variant: data.itemVariant || fixedItemName || '',
        category: TV_ORDERING_ITEM_CATEGORY[orderType],
        listId: '',
        listName: TV_ORDERING_LIST_NAME[orderType],
        serviceType: GTMItemServiceType.NewSale,
        index: 0,
        originalPrice,
        price,
        discount: getDiscount(originalPrice, price),
        quantity: DEFAULT_QUANTITY,
        commitmentType: normalizeCommitmentType(data.commitmentPeriod ? Math.round(data.commitmentPeriod) : undefined),
    };
};

export const mapTVOrderingToTrackingCommerce = (data: TVOrderingEventData): TrackingCommerce => {
    const orderType = getTVOrderingOrderType(data.orderType);

    return {
        orderType,
        segment: DEFAULT_SEGMENT,
        currency: DEFAULT_CURRENCY,
        value: data.price || 0,
        itemListName: TV_ORDERING_LIST_NAME[orderType],
        paymentMethod: PAYMENT_METHOD_WITH_PLAN,
        transactionId: data.transactionId || '',
        items: [mapTVOrderingToTrackingItem(data)],
    };
};
